import type { KioskFlowRepository } from '../data/kiosk-flow-repository';
import type { KioskFlowOperatorContext, KioskFlowPublishResult } from '../types/kiosk-flow-types';
import { PublishError } from '../types/kiosk-flow-errors';
import type { PushKioskFlowUseCase } from './PushKioskFlowUseCase';
import { assertOperator } from './flow-validation';

function asError(error: unknown, fallback: string): Error {
  return error instanceof Error ? error : new Error(fallback);
}

// Publishes the saved draft: prepare (backoffice validates and builds the JSON) → push to the
// kiosks → record the version. Nothing is recorded unless the push succeeded. Each failure is a
// PublishError naming its stage; a 'record' failure means the kiosks already run the new flow.
export class PublishKioskFlowUseCase {
  constructor(
    private readonly repository: KioskFlowRepository,
    private readonly pushKioskFlow: PushKioskFlowUseCase
  ) {}

  async execute(ctx: KioskFlowOperatorContext): Promise<KioskFlowPublishResult> {
    assertOperator(ctx);

    let json: string;
    try {
      json = await this.repository.preparePublish(ctx);
    } catch (error) {
      throw new PublishError('prepare', asError(error, 'Failed to prepare the flow for publishing'));
    }

    try {
      await this.pushKioskFlow.execute(ctx, json);
    } catch (error) {
      throw new PublishError('push', asError(error, 'Failed to push the flow to the kiosks'));
    }

    try {
      return await this.repository.recordVersion(ctx, json);
    } catch (error) {
      throw new PublishError('record', asError(error, 'Failed to record the published version'), json);
    }
  }
}
